export const loadBalancing = {
  id: 7,
  slug: 'load-balance-two-internet-links-on-mikrotik',
  title: 'Load Balancing and Failover on MikroTik using PCC (Per Connection Classifier)',
  imageURL: '/images/load-balancing.png',
  content: `
Managing internet redundancy and bandwidth utilization is essential for any ISP or network administrator. In this guide, we’ll walk through setting up **Load Balancing with Failover** using MikroTik’s **Per Connection Classifier (PCC)** method.

This configuration is based on a real-world setup using RouterOS v7 and assumes the following:

---

## ✅ Assumptions

| Component      | Configuration                               |
| -------------- | ------------------------------------------- |
| RouterOS       | Version 7                                   |
| WAN1 Interface | \`ether1\`, named \`WAN1\`, IP: \`192.168.1.254\` |
| WAN2 Interface | PPPoE client named \`pppoe-out1\`             |
| LAN Interface  | \`hotspot_pppoe\` (Hotspot/PPPoE clients)     |

---

## 🧠 What is PCC (Per Connection Classifier)?

**Per Connection Classifier (PCC)** is a powerful method used to **distribute connections** across multiple WANs based on client attributes like IP, source/destination address, and ports.

Think of PCC like a **smart traffic dispatcher**. It ensures:

- A user’s **sessions are consistently routed through one WAN** (no session breaks)
- Traffic is **split proportionally** based on rules (e.g., 50/50 distribution)

PCC is ideal where session consistency matters—like streaming, VoIP, and online banking.

---

## 🔧 Step-by-Step Setup

### 1. Create Custom Routing Tables

\`\`\`bash
/routing/table
add name=to_WAN1 fib
add name=to_WAN2 fib
\`\`\`

---

### 2. Add Routes to Each WAN Gateway

\`\`\`bash
/ip/route
add dst-address=0.0.0.0/0 gateway=192.168.1.1 routing-table=to_WAN1 check-gateway=ping comment="Route via WAN1"
add dst-address=0.0.0.0/0 gateway=pppoe-out1 routing-table=to_WAN2 check-gateway=ping comment="Route via WAN2"
\`\`\`

---

### 3. Configure Mangle Rules for PCC Load Balancing

\`\`\`bash
/ip/firewall/mangle

# Step 1: Mark new connections
add chain=prerouting in-interface=hotspot_pppoe connection-mark=no-mark \\
    action=mark-connection new-connection-mark=WAN1_conn \\
    per-connection-classifier=both-addresses:2/0 passthrough=yes

add chain=prerouting in-interface=hotspot_pppoe connection-mark=no-mark \\
    action=mark-connection new-connection-mark=WAN2_conn \\
    per-connection-classifier=both-addresses:2/1 passthrough=yes

# Step 2: Mark routing
add chain=prerouting in-interface=hotspot_pppoe connection-mark=WAN1_conn \\
    action=mark-routing new-routing-mark=to_WAN1 passthrough=yes

add chain=prerouting in-interface=hotspot_pppoe connection-mark=WAN2_conn \\
    action=mark-routing new-routing-mark=to_WAN2 passthrough=yes
\`\`\`

---

### 4. Add NAT (Masquerade) Rules

\`\`\`bash
/ip/firewall/nat
add chain=srcnat out-interface=WAN1 action=masquerade
add chain=srcnat out-interface=pppoe-out1 action=masquerade
\`\`\`

---

## 🛡️ Add Failover for Redundancy

\`\`\`bash
/ip/route
# Backup default routes with higher distance
add dst-address=0.0.0.0/0 gateway=192.168.1.1 distance=2 check-gateway=ping comment="Failover via WAN1"
add dst-address=0.0.0.0/0 gateway=pppoe-out1 distance=3 check-gateway=ping comment="Failover via WAN2"
\`\`\`

If one link goes down, the router automatically routes traffic through the backup.

---

## 📊 Verifying Load Balancing

Use MikroTik's built-in tools:

\`\`\`bash
/tool/torch interface=WAN1
/tool/torch interface=pppoe-out1
\`\`\`

You should see active traffic on both interfaces.

\`\`\`bash
/ip/firewall/connection/print where connection-mark~"WAN"
\`\`\`

---

## ✅ Benefits of This Setup

- ⚖️ Balanced usage across multiple ISPs  
- 🔄 Automatic failover for higher uptime  
- 🔐 Session persistence for uninterrupted connections  
- 📶 Ideal for Hotspot, PPPoE, and mixed LAN environments  

---

## 🤝 How NasRadius Complements PCC

While PCC helps balance traffic, it doesn’t manage users, billing, or authentication. That’s where **[NasRadius](https://nasradius.com/)** comes in:

- 🔐 PPPoE and Hotspot authentication using RADIUS  
- 📲 Integrated MPesa payments and voucher support  
- 📈 Monitor user sessions, data usage, and plan expiries  
- 🛠️ Push queue and bandwidth rules to Mikrotik routers  
- 🌍 Centralized remote management of multiple routers  

Together, PCC and NasRadius give you both **network efficiency** and **business automation**.

---

## 📌 Related Blog Posts

- [Setting Up PPPoE on Mikrotik](/blog/setup-pppoe-authentication-on-mikrotik)  
- [Remote Access to Mikrotik with NasRadius](/blog/remote-access-to-mikrotik-with-nasradius)  
- [What is RouterOS?](/blog/what-is-routeros)  

---

## 👨‍🔧 Final Thoughts

Load balancing using PCC with failover provides **resilient, stable, and scalable internet delivery**. Whether you’re running a Wi-Fi hotspot, WISP, or PPPoE network — this setup ensures better user experience and optimized bandwidth utilization.

If you're building an ISP with **NasRadius**, this is the kind of technical foundation you need for uptime, growth, and profitability.

---

## 🔗 Try NasRadius for Centralized Mikrotik Control

👉 [**Start with NasRadius**](https://nasradius.com/) and simplify your ISP operations — from user authentication and payments to automated failover routing.
  `
};
